const CUSTOM_OFFER_STATUSES = {
  PENDING: 'pending',
  WITHDRAWN: 'withdrawn',
  DECLINED: 'declined',
  ACCEPTED_PENDING_PAYMENT: 'accepted_pending_payment',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
};

const CUSTOM_OFFER_METADATA_KEY = 'customOffers';
const CUSTOM_OFFER_LATEST_ID_KEY = 'latestCustomOfferId';
const CUSTOM_OFFER_MESSAGE_TYPE = 'customOffer';
const CUSTOM_OFFER_MESSAGE_EVENTS = {
  CONFIRMED_BOOKING_RESCHEDULE: 'confirmedBookingReschedule',
};
const CUSTOM_OFFER_CURRENCY = 'THB';
const CUSTOM_OFFER_EXPIRY_HOURS = 48;
const MIN_CUSTOM_OFFER_AMOUNT_SUBUNITS = 100;
const CUSTOM_OFFER_AVAILABILITY_SEATS = 1;
// Backwards-compatible name for older custom-offer metadata/tests.
// Custom offers now create an explicit one-seat availability exception rather than a blocking exception.
const CUSTOM_OFFER_SLOT_BLOCK_SEATS = CUSTOM_OFFER_AVAILABILITY_SEATS;

const CUSTOM_OFFER_TRANSITIONS = {
  SEND: 'transition/send-custom-offer',
  WITHDRAW: 'transition/withdraw-custom-offer',
  DECLINE: 'transition/decline-custom-offer',
  ACCEPT: 'transition/accept-custom-offer',
  EXPIRE: 'transition/expire-custom-offer',
  RESCHEDULE: 'transition/reschedule-custom-offer',
  SEND_AFTER_BOOKING_REQUEST: 'transition/send-custom-offer-after-booking-request',
  WITHDRAW_AFTER_BOOKING_REQUEST: 'transition/withdraw-custom-offer-after-booking-request',
  DECLINE_AFTER_BOOKING_REQUEST: 'transition/decline-custom-offer-after-booking-request',
  ACCEPT_AFTER_BOOKING_REQUEST: 'transition/accept-custom-offer-after-booking-request',
  EXPIRE_AFTER_BOOKING_REQUEST: 'transition/expire-custom-offer-after-booking-request',
  RESCHEDULE_AFTER_BOOKING_REQUEST: 'transition/reschedule-custom-offer-after-booking-request',
  SEND_AFTER_DECLINED_BOOKING: 'transition/send-custom-offer-after-declined-booking',
  WITHDRAW_AFTER_DECLINED_BOOKING: 'transition/withdraw-custom-offer-after-declined-booking',
  DECLINE_AFTER_DECLINED_BOOKING: 'transition/decline-custom-offer-after-declined-booking',
  ACCEPT_AFTER_DECLINED_BOOKING: 'transition/accept-custom-offer-after-declined-booking',
  EXPIRE_AFTER_DECLINED_BOOKING: 'transition/expire-custom-offer-after-declined-booking',
  RESCHEDULE_AFTER_DECLINED_BOOKING: 'transition/reschedule-custom-offer-after-declined-booking',
  SEND_AFTER_CANCELLED_BOOKING: 'transition/send-custom-offer-after-cancelled-booking',
  WITHDRAW_AFTER_CANCELLED_BOOKING: 'transition/withdraw-custom-offer-after-cancelled-booking',
  DECLINE_AFTER_CANCELLED_BOOKING: 'transition/decline-custom-offer-after-cancelled-booking',
  ACCEPT_AFTER_CANCELLED_BOOKING: 'transition/accept-custom-offer-after-cancelled-booking',
  EXPIRE_AFTER_CANCELLED_BOOKING: 'transition/expire-custom-offer-after-cancelled-booking',
  RESCHEDULE_AFTER_CANCELLED_BOOKING: 'transition/reschedule-custom-offer-after-cancelled-booking',
  APPLY_TO_BOOKING_REQUEST: 'transition/apply-custom-offer-to-booking-request',
  APPLY_PAYMENT_TO_BOOKING_REQUEST: 'transition/apply-custom-offer-payment-to-booking-request',
  RESCHEDULE_PAYMENT: 'transition/reschedule-custom-offer-payment',
  RESCHEDULE_PREAUTHORIZED: 'transition/reschedule-custom-offer-preauthorized',
  RESCHEDULE_PAY_LATER_PAYMENT: 'transition/reschedule-custom-offer-pay-later-payment',
  RESCHEDULE_ACCEPTED_PENDING_PAYMENT:
    'transition/reschedule-custom-offer-accepted-pending-payment',
  RESCHEDULE_BOOKING: 'transition/reschedule-custom-offer-booking',
  CONFIRM_PAYMENT: 'transition/confirm-custom-offer-payment',
  OPERATOR_CONFIRM_PAYMENT: 'transition/operator-confirm-custom-offer-payment',
  COMPLETE_PAYMENT: 'transition/complete-custom-offer-payment',
  CONFIRM_PAY_LATER_PAYMENT: 'transition/confirm-custom-offer-pay-later-payment',
};

const CUSTOM_OFFER_RESCHEDULE_TRANSITIONS = [
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_PREAUTHORIZED,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_PAY_LATER_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_ACCEPTED_PENDING_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_BOOKING,
];

const CUSTOM_OFFER_PAYMENT_REQUEST_TRANSITIONS = [
  'transition/request-custom-offer-payment-after-inquiry',
  'transition/request-custom-offer-pay-later-payment-after-inquiry',
  CUSTOM_OFFER_TRANSITIONS.APPLY_TO_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.APPLY_PAYMENT_TO_BOOKING_REQUEST,
  'transition/retry-custom-offer-payment-after-failed',
  'transition/retry-custom-offer-pay-later-payment-after-failed',
];

const CUSTOM_OFFER_PAY_LATER_PAYMENT_REQUEST_TRANSITIONS = [
  'transition/request-custom-offer-pay-later-payment-after-inquiry',
  CUSTOM_OFFER_TRANSITIONS.APPLY_TO_BOOKING_REQUEST,
  'transition/retry-custom-offer-pay-later-payment-after-failed',
];

const CUSTOM_OFFER_INQUIRY_TRANSITIONS = [
  'transition/inquire',
  CUSTOM_OFFER_TRANSITIONS.SEND,
  CUSTOM_OFFER_TRANSITIONS.WITHDRAW,
  CUSTOM_OFFER_TRANSITIONS.DECLINE,
  CUSTOM_OFFER_TRANSITIONS.ACCEPT,
  CUSTOM_OFFER_TRANSITIONS.EXPIRE,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE,
];

// A pay-later request creates its booking before the provider responds. Custom-offer
// negotiation can safely remain in that unpaid state; card requests are intentionally
// excluded because their payment has already been authorised.
const CUSTOM_OFFER_BOOKING_REQUEST_TRANSITIONS = [
  'transition/request-pay-later-payment',
  'transition/request-pay-later-payment-after-inquiry',
  CUSTOM_OFFER_TRANSITIONS.SEND_AFTER_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.WITHDRAW_AFTER_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.DECLINE_AFTER_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.ACCEPT_AFTER_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.EXPIRE_AFTER_BOOKING_REQUEST,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_AFTER_BOOKING_REQUEST,
];

const CUSTOM_OFFER_CREATION_TRANSITIONS = [
  ...CUSTOM_OFFER_INQUIRY_TRANSITIONS,
  ...CUSTOM_OFFER_BOOKING_REQUEST_TRANSITIONS,
];

const DECLINED_BOOKING_CUSTOM_OFFER_TRANSITIONS = [
  CUSTOM_OFFER_TRANSITIONS.SEND_AFTER_DECLINED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.WITHDRAW_AFTER_DECLINED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.DECLINE_AFTER_DECLINED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.ACCEPT_AFTER_DECLINED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.EXPIRE_AFTER_DECLINED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_AFTER_DECLINED_BOOKING,
];

const CANCELLED_BOOKING_CUSTOM_OFFER_TRANSITIONS = [
  CUSTOM_OFFER_TRANSITIONS.SEND_AFTER_CANCELLED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.WITHDRAW_AFTER_CANCELLED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.DECLINE_AFTER_CANCELLED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.ACCEPT_AFTER_CANCELLED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.EXPIRE_AFTER_CANCELLED_BOOKING,
  CUSTOM_OFFER_TRANSITIONS.RESCHEDULE_AFTER_CANCELLED_BOOKING,
];

const DECLINED_INITIAL_BOOKING_TRANSITIONS = new Set([
  'transition/decline',
  'transition/operator-decline',
  'transition/decline-pay-later',
  'transition/operator-decline-pay-later',
  ...DECLINED_BOOKING_CUSTOM_OFFER_TRANSITIONS,
]);

const CANCELLED_INITIAL_BOOKING_TRANSITIONS = new Set([
  'transition/cancel',
  'transition/cancel-by-customer',
  'transition/cancel-by-provider',
  'transition/cancel-booking-request-payment',
  'transition/cancel-booking-request',
  'transition/cancel-pay-later-booking-request',
  ...CANCELLED_BOOKING_CUSTOM_OFFER_TRANSITIONS,
]);

// Marketplace payment-request transitions only create/update the booking. An Omise
// charge is created separately by our payment API, so request/retry transitions alone
// are not evidence that Omise was ever called.
const OMISE_PAYMENT_INITIATION_TRANSITIONS = new Set([
  'transition/confirm-payment',
  'transition/operator-confirm-payment',
  'transition/confirm-custom-offer-payment',
  'transition/operator-confirm-custom-offer-payment',
  'transition/payment-failed',
  'transition/confirm-pay-later-payment',
  'transition/operator-confirm-pay-later-payment',
  'transition/confirm-custom-offer-pay-later-payment',
  'transition/complete-custom-offer-payment',
]);

const OMISE_PAYMENT_EVIDENCE_KEYS = [
  'omiseChargeId',
  'chargeId',
  'chargeSacner',
  'qrCode',
  'promptPayQr',
  'authorizeUrl',
  'authorizeUri',
  'authorize_uri',
  'omiseSourceId',
  'sourceId',
  'sourceToken',
  'omiseToken',
  'tokenId',
  'paymentIntentId',
  'paymentSessionId',
  'paymentAuthorizationId',
];

const OMISE_PAYMENT_STATUS_KEYS = [
  'omiseChargeStatus',
  'chargeStatus',
  'paymentStatus',
  'paymentIntentStatus',
];

const NON_INITIATED_PAYMENT_STATUSES = new Set(['', 'none', 'new', 'notstarted', 'unpaid']);

const RETRYABLE_PAYMENT_STATUSES = new Set([
  'abandoned',
  'cancel',
  'cancelled',
  'canceled',
  'expired',
  'failed',
  'reversed',
  'voided',
]);

const ACTIVE_PAYMENT_STATUSES = new Set([
  'authorized',
  'created',
  'pending',
  'processing',
  'requiresaction',
]);

const CUSTOM_OFFER_PAYMENT_COMPLETED_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/operator-confirm-payment',
  'transition/confirm-pay-later-payment',
  'transition/operator-confirm-pay-later-payment',
  CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.OPERATOR_CONFIRM_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.COMPLETE_PAYMENT,
  CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
];

const NON_PAYABLE_TRANSACTION_TRANSITIONS = new Set([
  'transition/decline',
  'transition/operator-decline',
  'transition/decline-pay-later',
  'transition/operator-decline-pay-later',
  'transition/cancel',
  'transition/cancel-by-customer',
  'transition/cancel-by-provider',
  'transition/cancel-booking-request-payment',
  'transition/cancel-booking-request',
  'transition/cancel-pay-later-booking-request',
  'transition/acceptance-window-passed',
  'transition/pay-later-acceptance-window-passed',
  'transition/operator-acceptance-window-passed',
  'transition/operator-pay-later-acceptance-window-passed',
  'transition/auto-decline-booking-time-starts',
  'transition/expire',
  'transition/payment-window-passed',
  'transition/operator-payment-window-passed',
  'transition/complete',
  'transition/operator-complete',
  'transition/payout-complete',
  'transition/operator-payout-complete',
]);

const NON_RESCHEDULABLE_CUSTOM_OFFER_TRANSACTION_TRANSITIONS = new Set([
  ...NON_PAYABLE_TRANSACTION_TRANSITIONS,
  'transition/operator-expire-payment',
  'transition/complete',
  'transition/operator-complete',
  'transition/auto-complete-after-reschedule-booking',
  'transition/operator-complete-after-reschedule-booking',
  'transition/complete-after-cancel',
  'transition/operator-complete-after-cancel',
  'transition/dispute',
  'transition/operator-dispute',
  'transition/mark-received-from-disputed',
  'transition/payout-complete',
  'transition/operator-payout-complete',
  'transition/payout-complete-after-dispute',
  'transition/operator-payout-complete-after-dispute',
  'transition/review-1-by-provider',
  'transition/review-2-by-provider',
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
  'transition/expire-review-period',
  'transition/expire-provider-review-period',
  'transition/expire-customer-review-period',
]);

const TERMINAL_CUSTOM_OFFER_STATUSES = [
  CUSTOM_OFFER_STATUSES.WITHDRAWN,
  CUSTOM_OFFER_STATUSES.DECLINED,
  CUSTOM_OFFER_STATUSES.ACCEPTED,
  CUSTOM_OFFER_STATUSES.EXPIRED,
];

const ACTIONABLE_CUSTOM_OFFER_STATUSES = [
  CUSTOM_OFFER_STATUSES.PENDING,
  CUSTOM_OFFER_STATUSES.ACCEPTED_PENDING_PAYMENT,
];

const NON_CARD_PAYMENT_METHODS = ['promptpay', 'mobile_banking', 'mobile-banking'];

const deleteOverlappingAvailabilityExceptions = async ({
  sdk,
  listingId,
  startDateTime,
  endDateTime,
}) => {
  try {
    if (!sdk?.availabilityExceptions?.query || !sdk?.availabilityExceptions?.delete) {
      return [];
    }

    const response = await sdk.availabilityExceptions.query({
      listingId,
      start: safeDate(startDateTime),
      end: safeDate(endDateTime),
    });

    const exceptions = response?.data?.data || [];
    console.log('[custom-offers] overlapping availability exceptions found', {
      listingId: normalizeId(listingId),
      startDateTime: safeDate(startDateTime)?.toISOString(),
      endDateTime: safeDate(endDateTime)?.toISOString(),
      count: exceptions.length,
      exceptionIds: exceptions.map(exception => normalizeId(exception?.id)).filter(Boolean),
    });

    await Promise.all(
      exceptions.map(exception =>
        sdk.availabilityExceptions.delete({
          id: exception.id,
        })
      )
    );

    return exceptions;
  } catch (error) {
    console.warn(
      '[custom-offers] deleteOverlappingAvailabilityExceptions failed',
      error?.message || error
    );

    return [];
  }
};

const createCustomAvailabilityException = async ({
  sdk,
  listingId,
  startDateTime,
  endDateTime,
}) => {
  try {
    if (!sdk?.availabilityExceptions?.create) {
      return null;
    }

    const start = safeDate(startDateTime);
    const end = safeDate(endDateTime);
    console.log('[custom-offers] create availability exception request', {
      listingId: normalizeId(listingId),
      startDateTime: start?.toISOString(),
      endDateTime: end?.toISOString(),
      seats: CUSTOM_OFFER_AVAILABILITY_SEATS,
    });

    // Custom offers may use times outside the listing's weekly availability plan.
    // A one-seat availability exception explicitly opens this offered slot so Sharetribe's
    // speculative and real booking transitions can pass normal availability validation.
    const response = await sdk.availabilityExceptions.create(
      {
        listingId,
        start,
        end,
        seats: CUSTOM_OFFER_AVAILABILITY_SEATS,
      },
      { expand: true }
    );

    const availabilityExceptionId = normalizeId(response?.data?.data?.id);
    console.log('[custom-offers] create availability exception response', {
      listingId: normalizeId(listingId),
      availabilityExceptionId,
      seats: CUSTOM_OFFER_AVAILABILITY_SEATS,
    });

    return availabilityExceptionId;
  } catch (error) {
    console.warn(
      '[custom-offers] createCustomAvailabilityException failed',
      error?.message || error
    );
  }
};

const deleteCustomAvailabilityException = async ({ sdk, exceptionId }) => {
  const id = normalizeId(exceptionId);
  if (!sdk?.availabilityExceptions?.delete || !id) {
    return null;
  }

  try {
    console.log('[custom-offers] delete availability exception request', {
      availabilityExceptionId: id,
    });
    const response = await sdk.availabilityExceptions.delete(
      {
        id,
      },
      { expand: true }
    );
    console.log('[custom-offers] delete availability exception response', {
      availabilityExceptionId: id,
      deleted: true,
    });
    return response?.data?.data || true;
  } catch (error) {
    const status = error?.status || error?.statusCode || error?.response?.status;
    if (status === 404) {
      console.warn('[custom-offers] availability exception already removed', {
        availabilityExceptionId: id,
      });
      return null;
    }
    throw error;
  }
};

const unblockCustomAvailabilityException = async ({ sdk, offer }) => {
  return deleteCustomAvailabilityException({
    sdk,
    exceptionId: offer?.availabilityExceptionId || offer?.slotBlockId,
  });
};

const normalizeId = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && typeof value.uuid === 'string') {
    return value.uuid;
  }
  return null;
};

const safeDate = value => {
  if (!value) {
    return null;
  }

  // already Date object
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  // unix timestamp in milliseconds
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    const numericValue = Number(value);
    // Accept both Unix seconds and JavaScript milliseconds. Treating a valid
    // seconds timestamp as milliseconds would place it in 1970 and can make a
    // future Custom Offer look immediately expired.
    const timestamp = Math.abs(numericValue) < 1e11 ? numericValue * 1000 : numericValue;
    const unixDate = new Date(timestamp);

    return Number.isFinite(unixDate.getTime()) ? unixDate : null;
  }

  // ISO string fallback
  const date = new Date(value);

  return Number.isFinite(date.getTime()) ? date : null;
};

const addHours = (date, hours) => new Date(date.getTime() + hours * 60 * 60 * 1000);

const calculateCustomOfferExpiresAt = (createdAt, startDateTime) => {
  const createdDate = safeDate(createdAt) || new Date();
  const startDate = safeDate(startDateTime);
  const defaultExpiry = addHours(createdDate, CUSTOM_OFFER_EXPIRY_HOURS);

  if (startDate && startDate.getTime() < defaultExpiry.getTime()) {
    return startDate.toISOString();
  }

  return defaultExpiry.toISOString();
};

const isCustomOfferExpired = (offer, now = new Date()) => {
  if (!offer || offer.status !== CUSTOM_OFFER_STATUSES.PENDING) {
    return false;
  }
  const expiresAt = safeDate(offer.expiresAt);
  const currentTime = safeDate(now);
  return !!expiresAt && !!currentTime && currentTime.getTime() >= expiresAt.getTime();
};

const effectiveCustomOfferStatus = (offer, now = new Date()) => {
  if (isCustomOfferExpired(offer, now)) {
    return CUSTOM_OFFER_STATUSES.EXPIRED;
  }
  return offer?.status || null;
};

const normalizeCustomOfferAmount = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
  }
  if (value && typeof value.amount === 'number') {
    return Math.round(value.amount);
  }
  return null;
};

const moneyFromSubunits = (amount, currency = CUSTOM_OFFER_CURRENCY) => ({
  amount: normalizeCustomOfferAmount(amount) || 0,
  currency,
});

const formatCustomOfferMoney = (moneyOrAmount, locale = 'en-US') => {
  const amount = normalizeCustomOfferAmount(moneyOrAmount);
  const currency = moneyOrAmount?.currency || CUSTOM_OFFER_CURRENCY;
  const majorUnits = (amount || 0) / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(majorUnits);
  } catch (e) {
    return `฿${Math.round(majorUnits).toLocaleString(locale)}`;
  }
};

const getCustomOffers = transaction => {
  const metadataOffers = transaction?.attributes?.metadata?.[CUSTOM_OFFER_METADATA_KEY];
  const protectedDataOffers = transaction?.attributes?.protectedData?.[CUSTOM_OFFER_METADATA_KEY];
  const hasMetadataOffers = Array.isArray(metadataOffers) && metadataOffers.length;
  const hasProtectedDataOffers = Array.isArray(protectedDataOffers) && protectedDataOffers.length;

  if (!hasMetadataOffers) {
    return Array.isArray(protectedDataOffers) ? protectedDataOffers : metadataOffers || [];
  }

  if (!hasProtectedDataOffers) {
    return metadataOffers;
  }

  const protectedOffersById = protectedDataOffers.reduce((acc, offer) => {
    if (offer?.id) {
      acc[offer.id] = offer;
    }
    return acc;
  }, {});

  const mergedOffers = metadataOffers.map(metadataOffer => {
    const protectedOffer = protectedOffersById[(metadataOffer?.id)];
    if (!protectedOffer) {
      return metadataOffer;
    }

    const metadataUpdatedAt = safeDate(metadataOffer?.updatedAt || metadataOffer?.createdAt);
    const protectedUpdatedAt = safeDate(protectedOffer?.updatedAt || protectedOffer?.createdAt);
    const metadataStatus = effectiveCustomOfferStatus(metadataOffer);
    const protectedStatus = effectiveCustomOfferStatus(protectedOffer);
    const protectedIsNewer =
      protectedUpdatedAt &&
      (!metadataUpdatedAt || protectedUpdatedAt.getTime() >= metadataUpdatedAt.getTime());
    const protectedIsTerminal =
      TERMINAL_CUSTOM_OFFER_STATUSES.includes(protectedStatus) &&
      isActionableCustomOfferStatus(metadataStatus);

    return protectedIsNewer || protectedIsTerminal ? protectedOffer : metadataOffer;
  });

  const metadataOfferIds = new Set(metadataOffers.map(offer => offer?.id).filter(Boolean));
  const protectedOnlyOffers = protectedDataOffers.filter(
    offer => offer?.id && !metadataOfferIds.has(offer.id)
  );

  return [...mergedOffers, ...protectedOnlyOffers];
};

const getCustomOfferById = (transaction, offerId) => {
  const normalizedOfferId = normalizeId(offerId);
  return getCustomOffers(transaction).find(offer => offer?.id === normalizedOfferId) || null;
};

const getLatestCustomOffer = transaction => {
  const latestId =
    transaction?.attributes?.metadata?.[CUSTOM_OFFER_LATEST_ID_KEY] ||
    transaction?.attributes?.protectedData?.[CUSTOM_OFFER_LATEST_ID_KEY] ||
    transaction?.attributes?.protectedData?.latestCustomOffer?.id;
  const byId = latestId ? getCustomOfferById(transaction, latestId) : null;
  const offers = getCustomOffers(transaction);
  return (
    byId ||
    transaction?.attributes?.protectedData?.latestCustomOffer ||
    offers[offers.length - 1] ||
    null
  );
};

const getActiveCustomOffer = transaction => {
  return getCustomOffers(transaction)
    .slice()
    .reverse()
    .find(offer => isActionableCustomOfferStatus(effectiveCustomOfferStatus(offer)));
};

const isAcceptedCustomOfferStatus = status =>
  status === CUSTOM_OFFER_STATUSES.ACCEPTED_PENDING_PAYMENT ||
  status === CUSTOM_OFFER_STATUSES.ACCEPTED;

const getLatestAcceptedCustomOffer = transaction => {
  const latestOffer = getLatestCustomOffer(transaction);
  return isAcceptedCustomOfferStatus(effectiveCustomOfferStatus(latestOffer)) ? latestOffer : null;
};

const hasAcceptedCustomOffer = transaction =>
  getCustomOffers(transaction).some(offer => {
    return isAcceptedCustomOfferStatus(effectiveCustomOfferStatus(offer));
  });

const isTerminalCustomOfferStatus = status => TERMINAL_CUSTOM_OFFER_STATUSES.includes(status);

const isActionableCustomOfferStatus = status => ACTIONABLE_CUSTOM_OFFER_STATUSES.includes(status);

const transitionKey = transition =>
  typeof transition === 'string' ? transition : transition?.transition || transition?.name || null;

const isCustomOfferInquiryTransition = transition =>
  CUSTOM_OFFER_INQUIRY_TRANSITIONS.includes(transitionKey(transition));

const isCustomOfferBookingRequestTransition = transition =>
  CUSTOM_OFFER_BOOKING_REQUEST_TRANSITIONS.includes(transitionKey(transition));

const isCustomOfferCreationTransition = transition =>
  CUSTOM_OFFER_CREATION_TRANSITIONS.includes(transitionKey(transition));

const isDeclinedBookingCustomOfferTransition = transition =>
  DECLINED_BOOKING_CUSTOM_OFFER_TRANSITIONS.includes(transitionKey(transition));

const isCancelledBookingCustomOfferTransition = transition =>
  CANCELLED_BOOKING_CUSTOM_OFFER_TRANSITIONS.includes(transitionKey(transition));

const isCustomOfferPaymentRequestTransition = transition =>
  CUSTOM_OFFER_PAYMENT_REQUEST_TRANSITIONS.includes(transitionKey(transition));

const isCustomOfferPayLaterPaymentRequestTransition = transition =>
  CUSTOM_OFFER_PAY_LATER_PAYMENT_REQUEST_TRANSITIONS.includes(transitionKey(transition));

const isCustomOfferBookingTransaction = transaction => {
  const protectedData = transaction?.attributes?.protectedData || {};
  const metadata = transaction?.attributes?.metadata || {};
  const lastTransition = transaction?.attributes?.lastTransition;

  return !!(
    protectedData.isCustomOfferBooking ||
    protectedData.customOffer?.id ||
    protectedData.customOfferId ||
    metadata.customOfferPaymentContext ||
    metadata.customOfferId ||
    isCustomOfferPaymentRequestTransition(lastTransition) ||
    CUSTOM_OFFER_RESCHEDULE_TRANSITIONS.includes(transitionKey(lastTransition)) ||
    transitionKey(lastTransition) === CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAYMENT ||
    transitionKey(lastTransition) === CUSTOM_OFFER_TRANSITIONS.OPERATOR_CONFIRM_PAYMENT ||
    transitionKey(lastTransition) === CUSTOM_OFFER_TRANSITIONS.COMPLETE_PAYMENT ||
    transitionKey(lastTransition) === CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT
  );
};

const getCustomOfferPaymentMethod = transaction => {
  const protectedData = transaction?.attributes?.protectedData || {};
  const metadata = transaction?.attributes?.metadata || {};
  const processName = String(transaction?.attributes?.processName || '')
    .trim()
    .split('/')[0];

  return protectedData.paymentMethod || metadata.paymentMethod || processName || null;
};

const isCustomOfferNonCardPaymentTransaction = transaction =>
  isCustomOfferBookingTransaction(transaction) &&
  NON_CARD_PAYMENT_METHODS.includes(getCustomOfferPaymentMethod(transaction));

const getTransactionTransitionKeys = transaction => {
  const transitions = transaction?.attributes?.transitions || [];
  const lastTransition = transaction?.attributes?.lastTransition;
  return [...transitions.map(transitionKey), lastTransition].filter(Boolean);
};

const normalizePaymentStatus = status =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, '');

const PAID_PAYMENT_STATUSES = new Set([
  'paid',
  'authorized',
  'successful',
  'success',
  'succeeded',
  'captured',
  'confirmed',
]);

const isTruthyMetadataFlag = value => value === true || normalizePaymentStatus(value) === 'true';

const hasNonEmptyPaymentEvidence = data =>
  !!data &&
  OMISE_PAYMENT_EVIDENCE_KEYS.some(key => {
    const value = data[key];
    return value !== null && value !== undefined && value !== '' && value !== false;
  });

const hasPaymentStatusEvidence = data =>
  !!data &&
  OMISE_PAYMENT_STATUS_KEYS.some(key => {
    const value = normalizePaymentStatus(data[key]);
    return !NON_INITIATED_PAYMENT_STATUSES.has(value);
  });

const getPaymentStatuses = transaction => {
  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};

  return [metadata, protectedData]
    .flatMap(data => OMISE_PAYMENT_STATUS_KEYS.map(key => normalizePaymentStatus(data[key])))
    .filter(status => !NON_INITIATED_PAYMENT_STATUSES.has(status));
};

// omiseChargeStatus/chargeStatus describe the current state of the charge itself.
// They are more authoritative than historical workflow flags such as
// paymentConfirmed or paymentStatus, which can remain "true"/"authorized" after
// a card preauthorization has been reversed on booking decline.
const getAuthoritativeOmiseChargeStatus = transaction => {
  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  const candidates = [
    metadata.omiseChargeStatus,
    metadata.chargeStatus,
    protectedData.omiseChargeStatus,
    protectedData.chargeStatus,
  ];

  return candidates.map(normalizePaymentStatus).find(Boolean) || '';
};

const hasClosedOmiseCharge = transaction =>
  RETRYABLE_PAYMENT_STATUSES.has(getAuthoritativeOmiseChargeStatus(transaction));

const hasOmisePaymentBeenInitiated = transaction => {
  if (!transaction) {
    return false;
  }

  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  const transitionKeys = getTransactionTransitionKeys(transaction);

  return (
    hasNonEmptyPaymentEvidence(metadata) ||
    hasNonEmptyPaymentEvidence(protectedData) ||
    hasPaymentStatusEvidence(metadata) ||
    isTruthyMetadataFlag(metadata.paymentConfirmed) ||
    isTruthyMetadataFlag(metadata.promptpayPaid) ||
    isTruthyMetadataFlag(metadata.omisePaid) ||
    transitionKeys.some(transition => OMISE_PAYMENT_INITIATION_TRANSITIONS.has(transition))
  );
};

// A failed/expired/abandoned attempt is retryable and must not permanently close
// negotiation. Unknown charge evidence, an active charge, authorization, or a paid
// result still blocks creating a competing offer.
const hasBlockingOmisePayment = transaction => {
  if (!transaction) {
    return false;
  }

  // A declined card booking keeps its confirmation history, but the reversal
  // closes the original authorization. Any later custom offer uses a separate
  // linked transaction and payment, so the closed source charge must not block it.
  if (hasClosedOmiseCharge(transaction)) {
    return false;
  }

  if (isOmisePaymentPaid(transaction)) {
    return true;
  }

  const transitionKeys = getTransactionTransitionKeys(transaction);
  if (
    transitionKeys.some(transition =>
      CUSTOM_OFFER_PAYMENT_COMPLETED_TRANSITIONS.includes(transition)
    )
  ) {
    return true;
  }

  const statuses = getPaymentStatuses(transaction);
  if (statuses.some(status => ACTIVE_PAYMENT_STATUSES.has(status))) {
    return true;
  }
  if (statuses.length && statuses.every(status => RETRYABLE_PAYMENT_STATUSES.has(status))) {
    return false;
  }

  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  return hasNonEmptyPaymentEvidence(metadata) || hasNonEmptyPaymentEvidence(protectedData);
};

const getDeclinedOrCancelledBookingContext = transaction => {
  const lastTransition = transitionKey(transaction?.attributes?.lastTransition);
  if (DECLINED_INITIAL_BOOKING_TRANSITIONS.has(lastTransition)) {
    return 'declined';
  }
  if (CANCELLED_INITIAL_BOOKING_TRANSITIONS.has(lastTransition)) {
    return 'cancelled';
  }
  return null;
};

const isDefaultBookingTransaction = transaction =>
  String(transaction?.attributes?.processName || '')
    .trim()
    .split('/')[0] === 'default-booking';

const isDeclinedOrCancelledBookingWithoutPayment = transaction =>
  isDefaultBookingTransaction(transaction) &&
  !!getDeclinedOrCancelledBookingContext(transaction) &&
  !hasBlockingOmisePayment(transaction);

const canCreateCustomOffer = ({ transaction, isProvider, listingDeleted = false } = {}) => {
  if (!transaction || !isProvider || listingDeleted) {
    return false;
  }

  if (getActiveCustomOffer(transaction) || hasAcceptedCustomOffer(transaction)) {
    return false;
  }

  return (
    isCustomOfferCreationTransition(transaction?.attributes?.lastTransition) ||
    isDeclinedOrCancelledBookingWithoutPayment(transaction)
  );
};

const isOmisePaymentPaid = transaction => {
  const metadata = transaction?.attributes?.metadata || {};

  if (hasClosedOmiseCharge(transaction)) {
    return false;
  }

  return (
    isTruthyMetadataFlag(metadata.paymentConfirmed) ||
    isTruthyMetadataFlag(metadata.promptpayPaid) ||
    isTruthyMetadataFlag(metadata.omisePaid) ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(metadata.paymentStatus)) ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(metadata.omiseChargeStatus)) ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(metadata.chargeStatus))
  );
};

const isCustomOfferExplicitlyPaid = offer => {
  if (!offer) {
    return false;
  }

  return (
    !!offer.paidAt ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(offer.paymentStatus)) ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(offer.paymentIntentStatus)) ||
    PAID_PAYMENT_STATUSES.has(normalizePaymentStatus(offer.omiseChargeStatus))
  );
};

// A source booking can retain payment metadata and generic confirm-payment history
// from an earlier card authorization. Only transaction-wide payment evidence from
// the transaction that is actually collecting this offer's payment may complete the
// offer; the source transaction is completed through the offer-specific paid marker
// that is synced after the linked transaction succeeds.
const isCustomOfferPaymentContextForOffer = (transaction, offer = null) => {
  if (!transaction) {
    return false;
  }

  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  const transitionKeys = getTransactionTransitionKeys(transaction);
  const hasPaymentContext = !!(
    protectedData.isCustomOfferBooking ||
    protectedData.customOfferPaymentContext ||
    protectedData.customOffer?.id ||
    protectedData.customOfferId ||
    metadata.customOfferPaymentContext ||
    metadata.customOfferId ||
    transitionKeys.some(isCustomOfferPaymentRequestTransition) ||
    transitionKeys.some(transition =>
      [
        CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAYMENT,
        CUSTOM_OFFER_TRANSITIONS.OPERATOR_CONFIRM_PAYMENT,
        CUSTOM_OFFER_TRANSITIONS.COMPLETE_PAYMENT,
        CUSTOM_OFFER_TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
      ].includes(transition)
    )
  );

  if (!hasPaymentContext) {
    return false;
  }

  const offerId = normalizeId(offer?.id || getLatestCustomOffer(transaction)?.id);
  const paymentContextOfferId = normalizeId(
    protectedData.customOffer?.id ||
      protectedData.customOfferId ||
      metadata.customOfferId ||
      (hasPaymentContext ? getLatestCustomOffer(transaction)?.id : null)
  );

  return !offerId || !paymentContextOfferId || offerId === paymentContextOfferId;
};

const isCustomOfferPaymentCompleted = (transaction, offer = null) => {
  const latestOffer = offer || getLatestCustomOffer(transaction);
  if (isCustomOfferExplicitlyPaid(latestOffer)) {
    return true;
  }

  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  const paidOfferId = metadata.customOfferPaidId || protectedData.customOfferPaidId;
  if (
    paidOfferId &&
    (!latestOffer?.id || normalizeId(paidOfferId) === normalizeId(latestOffer.id))
  ) {
    return true;
  }

  if (!isCustomOfferPaymentContextForOffer(transaction, latestOffer)) {
    return false;
  }

  if (isOmisePaymentPaid(transaction)) {
    return true;
  }

  const transitionKeys = getTransactionTransitionKeys(transaction);
  return transitionKeys.some(transition =>
    CUSTOM_OFFER_PAYMENT_COMPLETED_TRANSITIONS.includes(transition)
  );
};

const isTransactionPayableForCustomOfferPayment = transaction => {
  const lastTransition = transitionKey(transaction?.attributes?.lastTransition);
  return !lastTransition || !NON_PAYABLE_TRANSACTION_TRANSITIONS.has(lastTransition);
};

const isLinkedCustomOfferPaymentRecovery = ({ transaction, offer } = {}) =>
  !!transaction &&
  !!offer &&
  !!getDeclinedOrCancelledBookingContext(transaction) &&
  // Once the server has created a linked payment transaction, historical payment
  // evidence on the immutable source booking must not hide navigation to it.
  (!!normalizeId(offer.relatedTransactionId) || !hasBlockingOmisePayment(transaction));

const isCustomOfferAwaitingPayment = ({ offer, transaction } = {}) => {
  if (!transaction || !isAcceptedCustomOfferStatus(effectiveCustomOfferStatus(offer))) {
    return false;
  }

  const latestOffer = getLatestCustomOffer(transaction);
  const isLatestOffer =
    !latestOffer?.id || !offer?.id || normalizeId(latestOffer.id) === normalizeId(offer.id);
  const hasPayableTransaction =
    isTransactionPayableForCustomOfferPayment(transaction) ||
    isLinkedCustomOfferPaymentRecovery({ transaction, offer });

  return (
    isLatestOffer && hasPayableTransaction && !isCustomOfferPaymentCompleted(transaction, offer)
  );
};

const isCustomOfferPaymentPending = transaction =>
  !!transaction &&
  isCustomOfferAwaitingPayment({
    transaction,
    offer: getLatestAcceptedCustomOffer(transaction),
  });

const hasReschedulableCustomOfferStatus = transaction => {
  const latestAcceptedOffer = getLatestAcceptedCustomOffer(transaction);
  return !!latestAcceptedOffer;
};

const isCustomOfferParentTransactionReschedulable = transaction => {
  const transitionKeys = getTransactionTransitionKeys(transaction);
  const hasBlockingTransition = transitionKeys.some(transition =>
    NON_RESCHEDULABLE_CUSTOM_OFFER_TRANSACTION_TRANSITIONS.has(transition)
  );

  if (hasBlockingTransition) {
    return false;
  }

  const bookingEnd = safeDate(
    transaction?.booking?.attributes?.displayEnd || transaction?.booking?.attributes?.end
  );
  if (bookingEnd && bookingEnd.getTime() <= Date.now()) {
    return false;
  }

  return true;
};

const canRescheduleCustomOfferBooking = ({ transaction, isProvider }) =>
  !!isProvider &&
  !!transaction &&
  hasReschedulableCustomOfferStatus(transaction) &&
  isCustomOfferParentTransactionReschedulable(transaction);

const hasPendingCustomOfferPaymentCharge = transaction => {
  const metadata = transaction?.attributes?.metadata || {};
  return (
    metadata.paymentConfirmed !== true &&
    metadata.omiseChargeStatus === 'pending' &&
    !!(metadata.omiseChargeId || metadata.chargeSacner || metadata.authorizeUrl)
  );
};

const canCompleteCustomOfferPayment = ({ offer, transaction, isProvider }) => {
  if (isProvider) {
    return false;
  }

  if (!transaction) {
    return false;
  }

  return isCustomOfferAwaitingPayment({ offer, transaction });
};

// Pending, abandoned, expired, and failed attempts remain resumable. Only successful
// payment evidence or a genuinely closed booking removes this action.
const shouldShowCustomOfferPaymentCta = params => canCompleteCustomOfferPayment(params);

const createCustomOfferId = () =>
  `co_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const buildCustomOffer = ({
  id,
  transactionId,
  listingId,
  listingTitle,
  hostId,
  creativeId,
  startDateTime,
  endDateTime,
  totalPrice,
  currency = CUSTOM_OFFER_CURRENCY,
  platformFee = null,
  netPayout = null,
  note = '',
  createdAt = new Date().toISOString(),
}) => {
  const amount = normalizeCustomOfferAmount(totalPrice);
  const start = safeDate(startDateTime);
  const end = safeDate(endDateTime);
  const totalPriceMoney = moneyFromSubunits(amount, currency);

  return {
    id: id || createCustomOfferId(),
    transactionId: normalizeId(transactionId),
    listingId: normalizeId(listingId),
    listingTitle: listingTitle || '',
    hostId: normalizeId(hostId),
    creativeId: normalizeId(creativeId),
    startDateTime: start ? start.toISOString() : null,
    endDateTime: end ? end.toISOString() : null,
    totalPrice: totalPriceMoney,
    totalPriceDisplay: formatCustomOfferMoney(totalPriceMoney),
    currency,
    platformFee: platformFee ? moneyFromSubunits(platformFee, currency) : null,
    netPayout: netPayout ? moneyFromSubunits(netPayout, currency) : null,
    note: String(note || '').trim(),
    status: CUSTOM_OFFER_STATUSES.PENDING,
    expiresAt: calculateCustomOfferExpiresAt(createdAt, startDateTime),
    createdAt,
    updatedAt: createdAt,
    history: [
      {
        action: CUSTOM_OFFER_STATUSES.PENDING,
        actorId: normalizeId(hostId),
        at: createdAt,
      },
    ],
  };
};

module.exports = {
  CUSTOM_OFFER_STATUSES,
  CUSTOM_OFFER_METADATA_KEY,
  CUSTOM_OFFER_LATEST_ID_KEY,
  CUSTOM_OFFER_MESSAGE_TYPE,
  CUSTOM_OFFER_MESSAGE_EVENTS,
  CUSTOM_OFFER_CURRENCY,
  CUSTOM_OFFER_EXPIRY_HOURS,
  MIN_CUSTOM_OFFER_AMOUNT_SUBUNITS,
  CUSTOM_OFFER_AVAILABILITY_SEATS,
  CUSTOM_OFFER_SLOT_BLOCK_SEATS,
  CUSTOM_OFFER_TRANSITIONS,
  CUSTOM_OFFER_PAYMENT_REQUEST_TRANSITIONS,
  CUSTOM_OFFER_PAY_LATER_PAYMENT_REQUEST_TRANSITIONS,
  CUSTOM_OFFER_INQUIRY_TRANSITIONS,
  CUSTOM_OFFER_BOOKING_REQUEST_TRANSITIONS,
  CUSTOM_OFFER_CREATION_TRANSITIONS,
  DECLINED_BOOKING_CUSTOM_OFFER_TRANSITIONS,
  CANCELLED_BOOKING_CUSTOM_OFFER_TRANSITIONS,
  CUSTOM_OFFER_RESCHEDULE_TRANSITIONS,
  TERMINAL_CUSTOM_OFFER_STATUSES,
  ACTIONABLE_CUSTOM_OFFER_STATUSES,
  CUSTOM_OFFER_PAYMENT_COMPLETED_TRANSITIONS,
  normalizeId,
  safeDate,
  calculateCustomOfferExpiresAt,
  isCustomOfferExpired,
  effectiveCustomOfferStatus,
  normalizeCustomOfferAmount,
  moneyFromSubunits,
  formatCustomOfferMoney,
  getCustomOffers,
  getCustomOfferById,
  getLatestCustomOffer,
  getActiveCustomOffer,
  getLatestAcceptedCustomOffer,
  hasAcceptedCustomOffer,
  isAcceptedCustomOfferStatus,
  isTerminalCustomOfferStatus,
  isActionableCustomOfferStatus,
  isCustomOfferInquiryTransition,
  isCustomOfferBookingRequestTransition,
  isCustomOfferCreationTransition,
  isDeclinedBookingCustomOfferTransition,
  isCancelledBookingCustomOfferTransition,
  isCustomOfferPaymentRequestTransition,
  isCustomOfferPayLaterPaymentRequestTransition,
  isCustomOfferBookingTransaction,
  getCustomOfferPaymentMethod,
  isCustomOfferNonCardPaymentTransaction,
  hasOmisePaymentBeenInitiated,
  hasBlockingOmisePayment,
  getDeclinedOrCancelledBookingContext,
  isDeclinedOrCancelledBookingWithoutPayment,
  canCreateCustomOffer,
  isOmisePaymentPaid,
  isCustomOfferPaymentContextForOffer,
  isCustomOfferPaymentCompleted,
  isTransactionPayableForCustomOfferPayment,
  isLinkedCustomOfferPaymentRecovery,
  isCustomOfferAwaitingPayment,
  isCustomOfferPaymentPending,
  hasReschedulableCustomOfferStatus,
  isCustomOfferParentTransactionReschedulable,
  canRescheduleCustomOfferBooking,
  hasPendingCustomOfferPaymentCharge,
  canCompleteCustomOfferPayment,
  shouldShowCustomOfferPaymentCta,
  createCustomOfferId,
  buildCustomOffer,
  deleteOverlappingAvailabilityExceptions,
  createCustomAvailabilityException,
  deleteCustomAvailabilityException,
  unblockCustomAvailabilityException,
};
